import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { clearAuth, getStoredUser, setAuth } from "@/lib/auth";
import { queryClient } from "@/lib/queryClient"; // MA-SESSION-CACHE-ISOLATION-117B: For cache clear on logout
import { useRunExitGuard } from "@/contexts/RunExitGuardContext";
import { useTenant } from "@/contexts/TenantContext";
import { usePermissions } from "@/hooks/usePermissions";
import { getNavigation } from "@/lib/navigationConfig";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { AccountSwitcher } from "@/components/AccountSwitcher";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import AppLauncher from "@/components/AppLauncher";
import ProfileUpdateDialog from "@/components/ProfileUpdateDialog";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import ExecutiveAssistDrawer from "@/components/ExecutiveAssistDrawer";
import NotificationCenter from "@/components/NotificationCenter";

import { toast } from "@/hooks/use-toast";
import ActivationStatusIndicator from "@/components/ActivationStatusIndicator";
import {
  LogOut,
  Compass,
  Menu,
  User as UserIcon,
  BookOpen,
  Shield,
  PanelLeftClose,
  PanelLeftOpen,
  Lock,
  Activity,
  Users,
  CalendarDays,
  Briefcase,
  BadgeDollarSign,
  Target,
  UserPen,
  Workflow,
  BarChart3,
  Megaphone,
  Route,
  Filter,
  Contact,
  FlaskConical,
  Layers,
  // MessageSquareQuote removed — Social Proof nav removed
} from "lucide-react";
import { LOGO_COLLAPSED_DATA, LOGO_PAGE_TITLE_DATA } from "@/assets/logoInline";
import PageTitleCard from "@/components/PageTitleCard";
import { PageTitleProvider, usePageTitleContext } from "@/contexts/PageTitleContext";
import { readBreadcrumbTrail, writeBreadcrumbTrail } from "@/lib/breadcrumbs";
import { buildTenantPath } from "@/lib/tenantPath";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

function MainContent({ children }) {
  const ctx = usePageTitleContext();
  const isEditor = ctx?.isEditorMode;
  
  return (
    <main
      className={
        isEditor
          ? "min-w-0 min-h-0 flex-1 overflow-hidden"
          : "min-w-0 min-h-0 flex-1 overflow-y-scroll pt-7 px-6 pb-12"
      }
      data-testid="app-main"
    >
      {isEditor ? (
        children
      ) : (
        <div className="space-y-6">
          <AppTitleCard />
          {children}
        </div>
      )}
    </main>
  );
}

// Route-local shell-mode gate: specialized full-height routes (e.g. Agent
// Workspace) may request suppression of the global top header via
// useHideGlobalHeader(). Left navigation and everything else in AppShell is
// unaffected — this only gates the <header> element itself.
export function GlobalTopHeaderGate({ children }) {
  const ctx = usePageTitleContext();
  if (ctx?.hideGlobalHeader) return null;
  return children;
}

function AppTitleCard() {
  const ctx = usePageTitleContext();
  // Hide in editor mode
  if (ctx?.isEditorMode || !ctx?.title) return null;
  return <PageTitleCard title={ctx.title} />;
}

// Section badge that hides in editor mode (MA-UI-054)
function PageTitleAwareSectionBadge({ section }) {
  const ctx = usePageTitleContext();
  // Hide section badge in editor mode for cleaner header
  if (ctx?.isEditorMode) return null;
  // P4-WP-9A.2: no section means no badge, rather than an empty pill. Mirrors
  // AppTitleCard, which already returns null on a falsy title.
  if (!section) return null;
  return (
    <Badge
      variant="secondary"
      className="bg-white/10 text-slate-100 ring-1 ring-white/10"
      data-testid="current-section-badge"
    >
      {section}
    </Badge>
  );
}

// Route -> shared PageTitleCard hero default title. Extracted as a pure,
// exported function (rather than left inline in the useMemo below) so it is
// directly unit-testable without rendering AppShell's full dependency graph.
//
// Checkpoint 3.5E: /app/warroom deliberately returns "" rather than a
// non-empty default. AnalyticsWarRoom.jsx has, since Checkpoint 1, always
// suppressed this hero for its entire route lifetime (it renders its own
// primary heading instead) -- but that suppression happens inside
// AnalyticsWarRoom's own effect, which cannot run until AnalyticsWarRoom
// itself mounts. RequireModule (PermissionGuard.jsx) gates that mount behind
// an async tenant/module/permissions loading check and renders a loading
// spinner in the meantime -- during which AppShell has already committed
// (and a real browser has already painted) a truthy "War Room" title, since
// PageTitleProvider seeds its `title` state directly from this default on
// first render. A route-local effect in AnalyticsWarRoom, no matter how
// early it fires, cannot suppress a hero that already painted before that
// component existed in the tree. Removing the non-empty seed at the source
// is the only fix that covers that entire pre-mount window.
export function getDefaultPageTitleForPath(path) {
  if (path.startsWith("/app/warroom")) return "";
  if (path.startsWith("/app/leads/")) return "Lead Details";
  if (path.startsWith("/app/appointments/")) return "Appointment Details";
  if (path.startsWith("/app/leads")) return "Leads";
  if (path.startsWith("/app/appointments")) return "Appointments";
  if (path.startsWith("/app/calendar")) return "Calendar";
  if (path.startsWith("/app/runs")) return "Runs";
  if (path.startsWith("/app/analytics")) return "Analytics";
  if (path.startsWith("/app/settings")) return "Settings";
  if (path.includes("/app/clients/") && path.includes("/products/")) return "Product Details";
  if (path.startsWith("/app/clients")) return "Clients";
  if (path.startsWith("/app/commission-setup")) return "Commission Profiles";
  if (path.startsWith("/app/admin")) return "Admin";
  if (path.startsWith("/app/scripts")) return "Script";
  // MA-ARCH-130: Profit Paths
  if (path.startsWith("/app/profit-paths/") && path !== "/app/profit-paths") return "Profit Path Details";
  if (path.startsWith("/app/profit-paths")) return "Profit Paths";
  if (path.startsWith("/app/funnels/") && path !== "/app/funnels") return "Funnel Details";
  if (path.startsWith("/app/funnels")) return "Funnels";
  if (path.startsWith("/app/landing-pages/") && path !== "/app/landing-pages") return "Landing Page Builder";
  if (path.startsWith("/app/landing-pages")) return "Landing Pages";
  if (path.startsWith("/app/campaigns")) return "Campaigns";
  if (path.startsWith("/app/advertising")) return "Advertising";
  if (path.startsWith("/app/experiments")) return "Experiments";
  // Social Proof and Copy Editor removed — now inline in Profit Path Builder
  // P4-WP-9A.2: "/app" is a TRANSIENT redirect-only route -- DefaultHomeRedirect
  // resolves it and navigates away; it never has a page of its own. It used to
  // map to "Playbooks", a legacy label from when the index WAS the Playbooks
  // page, and AppShell renders OUTSIDE ProtectedLayoutInner's tenant/permissions
  // gate, so that title was seeded into PageTitleProvider and painted while the
  // Outlet was still showing its spinner. Returning "" is the same fix, and for
  // the same documented reason, as /app/warroom above: AppTitleCard renders null
  // on a falsy title, so no hero exists during the resolution window at all.
  if (path === "/app" || path === "/app/") return "";
  return "MissionAssist";
}

export default function AppShell({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isInRun, requestLogout, requestNavigate } = useRunExitGuard();
  const { selectedTenantId, isViewingOtherTenant, isSuperAdmin, loading: tenantLoading } = useTenant();
  const [user, setUser] = useState(() => getStoredUser());
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const locationKey = `${location.pathname}${location.search}`;
  
  // State for tenant name (fetched from teams list)
  const [viewingTenantName, setViewingTenantName] = useState(null);
  
  // Fetch tenant name when viewing other tenant
  useEffect(() => {
    if (!isSuperAdmin || !isViewingOtherTenant || !selectedTenantId) {
      setViewingTenantName(null);
      return;
    }
    
    // Fetch teams to get the tenant name
    (async () => {
      try {
        const res = await api.get("/tenants");
        const team = res.data?.find(t => t.id === selectedTenantId);
        setViewingTenantName(team?.name || null);
      } catch {
        setViewingTenantName(null);
      }
    })();
  }, [isSuperAdmin, isViewingOtherTenant, selectedTenantId]);
  
  /**
   * P0 TENANT ISOLATION: Build navigation path with tenant_id query param
   * For SuperAdmins: ALWAYS include tenant_id (deterministic URLs)
   * For non-SuperAdmins: No tenant_id added
   */
  const buildNavPath = useCallback((basePath) => {
    // P0 TENANT ISOLATION: Use centralized tenant path builder
    // Guarantees no duplicate tenant_id and proper param handling
    if (isSuperAdmin && selectedTenantId) {
      return buildTenantPath(basePath, selectedTenantId);
    }
    return basePath;
  }, [isSuperAdmin, selectedTenantId]);

  const getStoredPath = (key, fallback) => {
    if (typeof window === "undefined") return fallback;
    return localStorage.getItem(key) || fallback;
  };

  const getModuleTarget = (modulePath, storageKey) => {
    if (location.pathname.startsWith(modulePath)) return modulePath;
    return getStoredPath(storageKey, modulePath);
  };

  const section = useMemo(() => {
    const path = location.pathname;
    if (path.includes("/play")) return "Runner";
    if (path.includes("/edit")) return "Builder";
    // P4-WP-9A.2: the section badge carried a SECOND copy of the same stale
    // "/app" -> Playbooks identity. Fixing only the hero above would have left
    // a "Playbooks" badge painted in the same transient window, so the
    // redirect-only route resolves to no section here too.
    if (path === "/app" || path === "/app/") return "";
    if (path.includes("/playbooks")) return "Playbooks";
    if (path.includes("/commission-setup")) return "Commission Profiles";
    if (path.includes("/admin")) return "Admin";
    if (path.includes("/warroom")) return "War Room";
    if (path.includes("/analytics")) return "Analytics";
    // MA-ARCH-130: Profit Paths
    if (path.startsWith("/app/profit-paths/") && path !== "/app/profit-paths") return "Profit Path Details";
    if (path.includes("/profit-paths")) return "Profit Paths";
    if (path.startsWith("/app/funnels/") && path !== "/app/funnels") return "Funnel Details";
    if (path.includes("/funnels")) return "Funnels";
    if (path.startsWith("/app/landing-pages/") && path !== "/app/landing-pages") return "Landing Page Builder";
    if (path.includes("/landing-pages")) return "Landing Pages";
    if (path.includes("/campaigns")) return "Campaigns";
    if (path.includes("/advertising")) return "Advertising";
    if (path.includes("/experiments")) return "Experiments";
    // Social Proof and Copy Editor removed — now inline in Profit Path Builder
    if (path.startsWith("/app/leads/") && path !== "/app/leads") return "Lead Details";
    if (path.includes("/leads")) return "Leads";
    if (path.startsWith("/app/appointments/") && path !== "/app/appointments") return "Appointment Details";
    if (path.includes("/appointments")) return "Appointments";
    if (path.includes("/calendar")) return "Calendar";
    if (path.includes("/app/clients/") && path.includes("/products/")) return "Product Details";
    if (path.startsWith("/app/clients/") && path !== "/app/clients") return "Client Details";
    if (path.includes("/clients")) return "Clients";
    return "Home";
  }, [location.pathname]);

  const defaultPageTitle = useMemo(
    () => getDefaultPageTitleForPath(location.pathname || ""),
    [location.pathname]
  );

  useEffect(() => {
    // Keep user fresh (also validates token)
    const run = async () => {
      try {
        const res = await api.get("/auth/me");
        setUser(res.data);
        // keep localStorage consistent
        const token = localStorage.getItem("scriptcompass_token");
        if (token) setAuth(token, res.data);
      } catch (e) {
        const status = e?.response?.status;
        // Only force logout on auth failures. Avoid logging users out on transient network errors.
        if (status === 401) {
          clearAuth();
          navigate("/login", { replace: true });
        }
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handler = (event) => {
      if (event?.detail) {
        setUser(event.detail);
      }
    };
    window.addEventListener("user-profile-updated", handler);
    return () => window.removeEventListener("user-profile-updated", handler);
  }, []);

  useEffect(() => {
    // P0 FIX: Only store pathname (not query params) to avoid URL doubling on tenant switch
    // The tenant_id will be added dynamically by buildNavPath
    if (location.pathname.startsWith("/app/leads")) {
      localStorage.setItem("last_path_leads", location.pathname);
    }
    if (location.pathname.startsWith("/app/appointments")) {
      localStorage.setItem("last_path_appointments", location.pathname);
    }
  }, [location.pathname]);

  useEffect(() => {
    // P0 FIX: Only use pathname for breadcrumb trail (not query params)
    // This ensures home path detection works and prevents tenant_id duplication
    const normalizedPath = location.pathname.endsWith("/") && location.pathname !== "/" 
      ? location.pathname.slice(0, -1) 
      : location.pathname;
    const homePaths = new Set(["/app/leads", "/app/appointments", "/app/clients"]);
    const isHome = homePaths.has(normalizedPath);
    const trail = readBreadcrumbTrail();

    let nextTrail = [];
    if (isHome) {
      nextTrail = [normalizedPath];
    } else {
      const existingIndex = trail.indexOf(normalizedPath);
      if (existingIndex >= 0) {
        nextTrail = trail.slice(0, existingIndex + 1);
      } else {
        nextTrail = [...trail, normalizedPath];
      }
    }

    writeBreadcrumbTrail(nextTrail);
  }, [location.pathname]);

  /**
   * MA-SESSION-CACHE-ISOLATION-117B: Hard logout with cache clear
   * 
   * Ensures deterministic session isolation by:
   * 1. Clearing React Query cache (permissions, modules, all tenant data)
   * 2. Clearing localStorage auth keys
   * 3. Clearing sessionStorage
   * 
   * This prevents User B from inheriting User A's cached permissions/UI state.
   */
  const onLogout = () => {
    if (isInRun) {
      requestLogout();
      return;
    }
    
    // 1. Clear auth localStorage (also clears navigation state)
    clearAuth();
    
    // 2. Clear React Query cache to prevent stale permission/data reuse
    queryClient.clear();
    
    // 3. Navigate to login
    toast({ title: "Signed out" });
    navigate("/login", { replace: true });
  };

  // MA-ARCH-201 Phase 3: Dynamic navigation based on permissions
  // Backend API is source of truth - no user.role checks
  const { effectiveModules, isResolvedForSelectedAccount, error: permissionsError } = usePermissions();
  
  // P4-PHASE1-NAV-REORG: one permission-filtered pass over the single
  // navigation definition (lib/navigationConfig.js), partitioned by placement.
  // `primary` renders the left icon rail, `launcher` renders the App Launcher
  // panel, and the mobile Sheet renders both — no surface declares its own
  // destination list, and no surface performs its own authorization.
  // P4-WP-9A.2 / P4-WP-9A.2a: do not render authorization-derived destinations
  // before authorization for THIS Account is resolved.
  //
  // `usePermissions` returns `effective_modules || []` whenever it has no data,
  // and AppShell renders OUTSIDE ProtectedLayoutInner's gate -- so the rail
  // could paint a PROVISIONAL set built from an empty module list. Module
  // destinations all vanished (correctly -- `[]` fails closed), while Agent
  // Workspace survived, because its GATE visibility consults the rollout
  // predicate and not `effectiveModules` at all. The result was a rail showing
  // Agent Workspace alone, which then changed once permissions settled.
  //
  // WP-9A.2 gated this on `permissionsLoading`, which was the WRONG lifecycle
  // signal: React Query v5's `isLoading` is `isPending && isFetching`, and this
  // query is disabled until an Account is selected, so a never-fetched query
  // reports `isLoading === false` with `data === undefined`. The flash survived
  // in exactly that window.
  //
  // The invariant is now `isResolvedForSelectedAccount` -- permissions data in
  // hand that BELONGS to the selected Account. That covers the disabled-query
  // window, the in-flight window, and the Account-switch window where a
  // previous Account's answer must not stand in for the new one. The hook owns
  // the definition; this component does not restate it.
  //
  // Empty lists are the same fail-closed value the surfaces already handle:
  // every consumer maps over them, and the launcher's `length > 0` check simply
  // omits its section. No provisional permissions are invented, no role default
  // is substituted, and Agent Workspace is NOT shown merely because its rollout
  // gate is open.
  //
  // Gating the single memo rather than the four render sites means a future
  // consumer cannot reintroduce the flash by reading these lists directly. The
  // SETTLED derivation is untouched: same call, same inputs, same order.
  // P4-WP-9A.2b: the rail's CONTENTS render atomically, or not at all.
  //
  // WP-9A.2a stopped authorization-derived destinations from rendering before
  // authorization resolved, but the Account Switcher is a SIBLING of that list
  // inside <aside data-testid="side-nav"> and was never gated. It renders its
  // own `accountsLoading` placeholder, so during hydration the rail showed one
  // isolated piece of chrome and nothing else -- two independent provisional
  // phases in one container.
  //
  // Readiness has two halves, and both are required:
  //
  //   tenantLoading           Account context (default-account resolution AND
  //                           the accounts fetch, combined in TenantContext).
  //                           Without this half, a fast permissions resolve on a
  //                           persisted Account would still expose the switcher's
  //                           loading placeholder on its own.
  //
  //   authorization TERMINAL  resolved for the selected Account, OR terminally
  //                           errored. Terminal -- not "resolved" -- because the
  //                           Account Switcher is recovery-capable Account-context
  //                           chrome, not a Product Module entitlement. If
  //                           /auth/me/access-context fails, `isResolvedForSelectedAccount`
  //                           stays false forever; a resolved-only gate would
  //                           permanently strand the user behind an empty rail with
  //                           no way to switch to an Account they can reach.
  //
  // Both inputs are account-scoped by construction, which is what makes this safe
  // across an Account switch: the permissions query key contains selectedTenantId,
  // so switching A -> B builds a NEW query whose `data` and `error` both start
  // empty. Neither A's success nor A's failure can make B look terminal.
  //
  // A background refetch does not blank a settled rail: `data` is still present,
  // so `isResolvedForSelectedAccount` stays true while `isFetching` is true.
  //
  // This gates RENDER TIMING only. It does not gate the Account Switcher on
  // effectiveModules, role, plan, or authorization success, and it changes no
  // settled behaviour -- once ready, the switcher obeys its own `shouldShow` and
  // navigation obeys the unchanged getNavigation derivation.
  const authorizationIsTerminal = isResolvedForSelectedAccount || !!permissionsError;
  const railContentsReady = !tenantLoading && authorizationIsTerminal;

  const { primary: primaryNavItems, launcher: launcherNavItems } = useMemo(
    () =>
      isResolvedForSelectedAccount
        ? getNavigation(effectiveModules, location.pathname, buildNavPath, selectedTenantId)
        : { primary: [], launcher: [] },
    [isResolvedForSelectedAccount, location.pathname, effectiveModules, buildNavPath, selectedTenantId]
  );

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Default collapsed on first load; afterward, persist per user.
    try {
      const raw = localStorage.getItem("missionassist_sidebar_collapsed");
      return raw === null ? true : raw === "true";
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(
        "missionassist_sidebar_collapsed",
        String(sidebarCollapsed)
      );
    } catch {
      // ignore
    }
  }, [sidebarCollapsed]);

  useEffect(() => {
    const img = new Image();
    img.src = LOGO_COLLAPSED_DATA;
    const img2 = new Image();
    img2.src = LOGO_PAGE_TITLE_DATA;
  }, []);

  const leftRailWidth = sidebarCollapsed ? "w-[96px]" : "w-[240px]";
  const iconRailWidth = "w-[96px]";

  return (
    <PageTitleProvider defaultTitle={defaultPageTitle}>
      <div className="flex h-screen overflow-hidden">
      {/* Global left rail column: owns the brand logo AND navigation
          together, as one self-contained full-height column that is a
          direct child of the outermost shell row. Its vertical origin is
          always the top of the viewport — it is a sibling of the header
          column below, not nested inside it — so suppressing the
          horizontal header (see GlobalTopHeaderGate / useHideGlobalHeader)
          can never shift the rail's geometry. The logo and nav items are
          real, normal-flow siblings within this column (not an absolutely
          positioned overlay with a computed clearance), so their spacing
          is a fixed, structural gap rather than a coincidence of stacked
          heights from unrelated elements. */}
      <div
        className={
          "relative z-20 hidden shrink-0 flex-col overflow-hidden sm:flex " + leftRailWidth
        }
        data-testid="global-rail-column"
      >
        <div className="absolute inset-0 bg-black/40" aria-hidden data-testid="sidebar-underlay" />

        <div className={"relative z-30 flex shrink-0 items-center justify-center pt-1 " + iconRailWidth}>
          <Link
            to="/app"
            className="block"
            data-testid="brand-link"
            style={{ maxHeight: '100px', overflow: 'hidden' }}
          >
            <img
              src={LOGO_COLLAPSED_DATA}
              alt="Logo"
              className="h-[100px] w-auto object-contain"
              style={{ transform: 'scale(1.25)', transformOrigin: 'center center' }}
              width={100}
              height={100}
              loading="eager"
              decoding="async"
              fetchPriority="high"
              data-testid="logo"
            />
          </Link>
        </div>

        <aside className="relative z-20 flex min-h-0 flex-1 flex-col overflow-hidden" data-testid="side-nav">
          {/* P4-WP-9A.2b: the <aside> itself always renders, so the rail keeps its
              structural geometry (width comes from global-rail-column above) and
              the main content never shifts. Only its CONTENTS wait for readiness,
              and they appear together. */}
          {railContentsReady && (
            <>
          {/* MA-PHASE11A: Account Switcher at Top of Left Rail */}
          <div className="shrink-0 pt-1 pb-1 relative z-30">
            <AccountSwitcher collapsed={sidebarCollapsed} />
          </div>

          {/* P4-PHASE1: Primary Rail. Renders the `primary` partition only —
              launcher applications no longer occupy persistent rail slots.
              The rail defaults to collapsed, so each icon carries an
              aria-label plus a Tooltip (existing design-system primitive)
              naming its destination; the tooltip is suppressed when the rail
              is expanded, where the text label is already visible. */}
          <div className="flex-1 overflow-y-auto">
            <TooltipProvider delayDuration={200}>
              <div className="space-y-2">
                {primaryNavItems.map((it) => {
                  const link = (
                    <Link
                      to={it.to}
                      aria-label={it.label}
                      aria-current={it.active ? "page" : undefined}
                      onClick={(e) => {
                        if (isInRun) {
                          e.preventDefault();
                          requestNavigate(it.to);
                        }
                      }}
                      className={
                        "group grid w-full items-center rounded-xl text-sm text-slate-100 transition " +
                        (sidebarCollapsed ? "grid-cols-[96px]" : "grid-cols-[96px_1fr]")
                      }
                      data-testid={it.testid}
                    >
                      <div className="flex h-10 w-[96px] items-center justify-center">
                        <div
                          className={
                            "flex h-10 w-10 items-center justify-center rounded-full transition " +
                            (it.active ? "bg-indigo-500/20" : "") +
                            " group-hover:bg-indigo-500/10"
                          }
                          data-testid={`nav-icon-pill-${it.testid}`}
                        >
                          <it.icon className="h-[22px] w-[22px] text-slate-100" />
                        </div>
                      </div>
                      {!sidebarCollapsed ? (
                        <div className="min-w-0 pr-3">
                          <div className="truncate">{it.label}</div>
                        </div>
                      ) : null}
                    </Link>
                  );

                  if (!sidebarCollapsed) {
                    return <React.Fragment key={it.to}>{link}</React.Fragment>;
                  }

                  return (
                    <Tooltip key={it.to}>
                      <TooltipTrigger asChild>{link}</TooltipTrigger>
                      <TooltipContent side="right" data-testid={`nav-tooltip-${it.testid}`}>
                        {it.label}
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            </TooltipProvider>
          </div>

          <div className="shrink-0 pb-6" data-testid="side-nav-footer">
            <Button
              variant="ghost"
              className={
                "group grid w-full items-center rounded-xl bg-transparent text-slate-100 transition hover:bg-transparent " +
                (sidebarCollapsed ? "grid-cols-[96px]" : "grid-cols-[96px_1fr]")
              }
              onClick={() => setSidebarCollapsed((v) => !v)}
              data-testid="sidebar-toggle"
            >
              <div className="flex h-10 w-[96px] items-center justify-center">
                <div className="flex h-10 w-10 items-center justify-center rounded-full transition group-hover:bg-indigo-500/10">
                  {sidebarCollapsed ? (
                    <PanelLeftOpen className="h-5 w-5 text-slate-100" />
                  ) : (
                    <PanelLeftClose className="h-5 w-5 text-slate-100" />
                  )}
                </div>
              </div>
              {!sidebarCollapsed ? (
                <div className="pr-3 text-left text-sm group-hover:text-slate-100">
                  Collapse
                </div>
              ) : null}
            </Button>
          </div>
            </>
          )}
        </aside>
      </div>

      {/* Right shell column: horizontal header (route-suppressible) above
          the main content area. This column's own top is where the header
          would start — it has no bearing on the rail column beside it. */}
      <div className="flex h-full min-w-0 flex-1 flex-col overflow-hidden">
      <GlobalTopHeaderGate>
      <header
        className="relative z-30 shrink-0 bg-transparent"
        data-testid="app-header"
      >
        <div className="flex w-full items-center justify-end gap-3 px-6 py-3">
            <Sheet>
              <SheetTrigger asChild>
                <Button
                  variant="secondary"
                  className="sm:hidden bg-white/10 text-slate-100 hover:bg-white/15"
                  data-testid="mobile-nav-open"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-slate-950 text-slate-100 ring-1 ring-white/10">
                <SheetHeader>
                  <SheetTitle className="sr-only">Menu</SheetTitle>
                </SheetHeader>

                {/* P4-PHASE1: mobile mirrors the desktop Primary/Launcher
                    distinction as two sections of one list, rendered from the
                    same permission-filtered partitions the desktop uses. The
                    App Launcher control itself is desktop-only, so "More Apps"
                    is what keeps launcher-only applications reachable here —
                    no launcher destination is stranded on mobile, and there is
                    no separate mobile application list. */}
                <div className="mt-6 space-y-2 px-2" data-testid="mobile-nav-links">
                  {primaryNavItems.map((it) => (
                    <SheetClose asChild key={it.to}>
                      <Link
                        to={it.to}
                        onClick={(e) => {
                          if (isInRun) {
                            e.preventDefault();
                            requestNavigate(it.to);
                          }
                        }}
                        className={
                          "flex items-center gap-3 rounded-xl px-4 py-3 text-sm transition " +
                          (it.active
                            ? "bg-indigo-500/20 text-indigo-100 hover:ring-1 hover:ring-inset hover:ring-white/10"
                            : "text-slate-100 hover:bg-white/10 hover:ring-1 hover:ring-inset hover:ring-white/10")
                        }
                        data-testid={`mobile-${it.testid}`}
                      >
                        <it.icon className="h-4 w-4" />
                        {it.label}
                      </Link>
                    </SheetClose>
                  ))}

                  {launcherNavItems.length > 0 ? (
                    <div className="pt-4" data-testid="mobile-nav-more-apps">
                      <div className="mb-2 border-t border-white/10 pt-4 px-4 text-[11px] font-semibold uppercase tracking-wider text-slate-200/60">
                        More Apps
                      </div>
                      <div className="space-y-2">
                        {launcherNavItems.map((it) => (
                          <SheetClose asChild key={it.to}>
                            <Link
                              to={it.to}
                              onClick={(e) => {
                                if (isInRun) {
                                  e.preventDefault();
                                  requestNavigate(it.to);
                                }
                              }}
                              className={
                                "flex items-center gap-3 rounded-xl px-4 py-3 text-sm transition " +
                                (it.active
                                  ? "bg-indigo-500/20 text-indigo-100 hover:ring-1 hover:ring-inset hover:ring-white/10"
                                  : "text-slate-100 hover:bg-white/10 hover:ring-1 hover:ring-inset hover:ring-white/10")
                              }
                              data-testid={`mobile-${it.testid}`}
                            >
                              <it.icon className="h-4 w-4" />
                              {it.label}
                            </Link>
                          </SheetClose>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              </SheetContent>
            </Sheet>

            {/* Section badge - Hidden in editor mode (MA-UI-054) */}
            <PageTitleAwareSectionBadge section={section} />

            {/* P0 TENANT ISOLATION: Show viewing indicator when SuperAdmin views other tenant */}
            {isSuperAdmin && isViewingOtherTenant && viewingTenantName && (
              <Badge
                variant="outline"
                className="border-amber-500/50 bg-amber-500/10 text-amber-200"
                data-testid="tenant-context-indicator"
              >
                Viewing: {viewingTenantName}
              </Badge>
            )}

            {/* MA-UI-078: Global Activation Status Indicator */}
            <ActivationStatusIndicator />

            {/* Checkpoint 3.4C: ExecutiveAssist moved from a standalone
                text-pill trigger into the global utility cluster, icon-only,
                positioned beside the notification bell (same
                h-10 w-10 rounded-full circular treatment as
                notification-bell/user-menu-trigger) — only visible on War
                Room, unchanged. */}
            {location.pathname.includes("/warroom") && (
              <ExecutiveAssistDrawer
                period="mtd"
                vertical={null}
                metaContext={null}
              />
            )}

            {/* P4-PHASE1: MissionAssist App Launcher — immediately before the
                notification bell. It lives inside GlobalTopHeaderGate along
                with every other header control, so routes that intentionally
                suppress the global header (useHideGlobalHeader — Agent
                Workspace shells) suppress the launcher too; it never appears
                independently of the header. */}
            <AppLauncher
              items={launcherNavItems}
              onNavigate={(event, to) => {
                if (isInRun) {
                  event.preventDefault();
                  requestNavigate(to);
                }
              }}
            />

            {/* MA-OPS-109: Notification Center */}
            <NotificationCenter />

            <Separator orientation="vertical" className="h-8 bg-white/10" />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="secondary"
                  className="h-10 w-10 rounded-full bg-white/10 p-0 text-slate-100 ring-1 ring-white/10 hover:bg-white/15"
                  data-testid="user-menu-trigger"
                >
                  <UserIcon className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64 bg-slate-950 text-slate-100 ring-1 ring-white/10" data-testid="user-menu-content">
                <DropdownMenuLabel className="space-y-1">
                  <div className="text-sm font-semibold text-slate-100" data-testid="user-menu-name">
                    {user?.name || ""}
                  </div>
                  <div className="text-xs text-slate-200/70" data-testid="user-menu-role">
                    {user?.role === "super_admin"
                      ? "Super Admin"
                      : user?.role === "admin"
                        ? "Admin"
                        : user?.role === "booking_agent"
                          ? "Booking Agent"
                          : "Sales Agent"}
                  </div>
                  <div className="text-xs text-slate-200/70" data-testid="user-menu-email">
                    {user?.email || ""}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <ProfileUpdateDialog
                  testIdPrefix="avatar-profile"
                  onUpdated={(updated) => setUser(updated)}
                  trigger={
                    <DropdownMenuItem
                      onSelect={(event) => event.preventDefault()}
                      data-testid="avatar-profile-menu-item"
                    >
                      <UserPen className="mr-2 h-4 w-4" /> Update Profile
                    </DropdownMenuItem>
                  }
                />
                <DropdownMenuItem 
                  onSelect={() => setChangePasswordOpen(true)}
                  data-testid="change-password-menu-item"
                >
                  <Lock className="mr-2 h-4 w-4" /> Change Password
                </DropdownMenuItem>
                {/* P4-PHASE1: Settings moved out of this menu and into the App
                    Launcher, where it is subject to the same effective-module
                    rule as every other destination. The entry here rendered
                    unconditionally, with no `settings` check. This dropdown is
                    now personal/session actions only. */}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onLogout} data-testid="user-menu-logout">
                  <LogOut className="mr-2 h-4 w-4" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
        </div>
      </header>
      </GlobalTopHeaderGate>

      <div className="flex min-h-0 flex-1 w-full" data-testid="app-shell-body">
        <MainContent>{children}</MainContent>
      </div>

      {/* footer removed for cleaner white-label layout */}
      </div>
    </div>
    
    {/* Change Password Dialog */}
    <ChangePasswordDialog 
      open={changePasswordOpen} 
      onOpenChange={setChangePasswordOpen} 
    />
    </PageTitleProvider>
  );
}
